import { CalendarDays, Receipt, ShoppingCart, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useI18n } from "../../../i18n";
import { fmtInt, fmtMoney } from "../../../lib/format";
import { pct } from "../../../lib/xutil";
import { useApi } from "../../customer/hooks";
import { StatCard } from "../../../components/common/StatCard";
import { PageHead, Panel } from "../../../components/common/Page";
import { ScheduledSyncNote } from "../../../components/common/ScheduledSyncNote";
import { StatusBadge } from "../../../components/common/StatusBadge";
import { SkeletonBlock } from "../../../components/common/EmptyState";
import { GREEN } from "../../customer/insights/Charts";
import { useAuth } from "../../../context/AuthContext";
import { hasPerm } from "../../../lib/perm";
import { SoftOneBadge } from "../softone/SoftOneBadge";
import { SalesmenPanel } from "./SalesmenPanel";

const Top = ({ rows, name, testId, empty }) => (
  <ol className="space-y-2" data-testid={testId}>
    {!rows.length && <li className="text-sm text-slate-400" data-testid={`${testId}-empty`}>{empty}</li>}
    {rows.map((r, i) => {
      const max = rows[0]?.net || 1;
      return (
        <li key={r.code} className="text-sm">
          <div className="flex justify-between gap-2"><span className="truncate text-navy">{i + 1}. {name(r)}</span><b className="tabular-nums text-navy">{fmtMoney(r.net)}</b></div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand" style={{ width: `${(r.net / max) * 100}%` }} /></div>
        </li>
      );
    })}
  </ol>
);

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const r = useApi("dashboard", "/staff/dashboard", undefined, { staleTime: 0, refetchInterval: 120000 });
  if (r.isLoading || !r.data) return <SkeletonBlock className="h-80" />;
  const d = r.data;
  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <PageHead title={t("x.dash.title")} sub={d.scope === "own" ? t("x.dash.ownScope") : t("x.dash.sub")} />
      {hasPerm(user, "softone:admin") && <SoftOneBadge />}
      {hasPerm(user, "softone:admin") && <ScheduledSyncNote s={d.scheduled_sync} />}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard testId="dash-today" icon={CalendarDays} label={t("x.dash.today")} value={fmtMoney(d.today.net)} hint={`${fmtInt(d.today.orders)} · ${t("x.dash.net")}`} />
        <StatCard testId="dash-mtd" icon={TrendingUp} label={t("x.dash.mtd")} value={fmtMoney(d.mtd.net)} tone="green" hint={t("x.dash.net")} />
        <StatCard testId="dash-orders" icon={ShoppingCart} label={t("x.dash.orders")} value={fmtInt(d.mtd.orders)} />
        <StatCard testId="dash-avg" icon={Receipt} label={t("x.dash.avg")} value={d.mtd.avg_order == null ? "—" : fmtMoney(d.mtd.avg_order)} />
      </div>
      <Panel title={t("x.dash.daily")} testId="dash-daily">
        <div className="h-56"><ResponsiveContainer><BarChart data={d.daily}><CartesianGrid vertical={false} stroke="#eef1f4" />
          <XAxis dataKey="date" tickFormatter={(v) => v.slice(8)} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={50} />
          <Tooltip formatter={(v) => fmtMoney(v)} labelFormatter={(v) => v.split("-").reverse().join("/")} /><Bar dataKey="net" fill={GREEN} radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
      </Panel>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t("x.dash.topCustomers")}><Top rows={d.top_customers} name={(x) => x.name} testId="dash-top-customers" empty={t("common.noDataMonth")} /></Panel>
        <Panel title={t("x.dash.topProducts")}><Top rows={d.top_products} name={(x) => x[`name_${lang}`]} testId="dash-top-products" empty={t("common.noDataMonth")} /></Panel>
        <SalesmenPanel />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t("x.dash.adoption")} testId="dash-adoption">
          <dl className="grid grid-cols-3 gap-3 text-sm">
            <div><dt className="text-xs text-slate-500">{t("x.dash.activeCustomers")}</dt><dd className="text-xl font-semibold text-navy" data-testid="dash-active">{d.adoption.active_customers}/{d.adoption.customers}</dd></div>
            <div><dt className="text-xs text-slate-500">{t("x.dash.viaApp")}</dt><dd className="text-xl font-semibold text-navy">{d.adoption.orders_via_app}</dd></div>
            <div><dt className="text-xs text-slate-500">{t("x.dash.appShare")}</dt><dd className="text-xl font-semibold text-brand" data-testid="dash-app-share">{pct(d.adoption.app_share_pct)}</dd></div>
          </dl>
        </Panel>
        <Panel title={t("x.dash.openByStatus")} testId="dash-open">
          <ul className="flex flex-wrap gap-2">{d.open_by_status.map((s) => <li key={s.status} className="flex items-center gap-2 rounded-full bg-canvas py-1 pl-1 pr-3 text-sm"><StatusBadge status={s.status} /><b className="tabular-nums text-navy">{s.count}</b></li>)}</ul>
        </Panel>
      </div>
    </div>
  );
}
